import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { borders, brand } from '../dashboardSales.styles';

function transactionDetailUrl(id: string) {
  const base = window.location.href.split('#')[0];
  return `${base}#/transactions/${id}`;
}

export type SaleCategory = 'initial' | 'renewal' | 'plan_change' | 'other';

const SEGMENT_KEYS: SaleCategory[] = [
  'initial',
  'renewal',
  'plan_change',
  'other',
];

const CATEGORY_LABELS: Record<SaleCategory, string> = {
  initial: 'New subs',
  renewal: 'Renewals',
  plan_change: 'Plan changes',
  other: 'Other',
};

const CATEGORY_COLORS: Record<SaleCategory, string> = {
  initial: '#1976d2',
  renewal: '#2e7d32',
  plan_change: '#ed6c02',
  other: '#757575',
};

export interface SalesPeriodPoint {
  period: string;
  label: string;
  revenue: number;
  initial: number;
  renewal: number;
  plan_change: number;
  other: number;
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(value);
}

/** Pinned panel below the chart — stays open so View buttons are easy to click. */
export function SalesPeriodDetailPanel({
  period,
  onViewTransactions,
  onClose,
}: {
  period: SalesPeriodPoint | null;
  onViewTransactions: (
    periodKey: string,
    category: SaleCategory,
    periodLabel: string,
    count: number,
  ) => void;
  onClose: () => void;
}) {
  if (!period) return null;

  const segments = SEGMENT_KEYS.filter((key) => period[key] > 0);
  if (segments.length === 0) return null;

  return (
    <Paper
      variant="outlined"
      sx={{
        mt: 2,
        p: 2,
        borderRadius: 2,
        border: '1px solid',
        borderColor: borders.strong,
        borderLeft: `4px solid ${brand.purple}`,
        bgcolor: brand.purpleLight,
        boxShadow: '0 2px 8px rgba(55, 71, 79, 0.06)',
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        sx={{ mb: 1.5 }}
      >
        <Box>
          <Typography
            variant="subtitle2"
            fontWeight={700}
            sx={{ color: brand.slate }}
          >
            {period.label}
          </Typography>
          {period.revenue > 0 && (
            <Typography variant="caption" color="text.secondary">
              Total revenue: {formatCurrency(period.revenue)}
            </Typography>
          )}
        </Box>
        <IconButton
          size="small"
          aria-label="Close period details"
          onClick={onClose}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      <Box display="flex" flexDirection="column" gap={1}>
        {segments.map((category) => {
          const count = period[category];
          return (
            <Box
              key={category}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                py: 0.5,
              }}
            >
              <Box display="flex" alignItems="center" gap={1.5} minWidth={0}>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    bgcolor: CATEGORY_COLORS[category],
                    flexShrink: 0,
                  }}
                />
                <Typography variant="body2">
                  {CATEGORY_LABELS[category]}: {count}
                </Typography>
              </Box>
              <Button
                size="small"
                variant="contained"
                disableElevation
                sx={{
                  bgcolor: CATEGORY_COLORS[category],
                  '&:hover': { filter: 'brightness(0.92)' },
                }}
                onClick={() =>
                  onViewTransactions(
                    period.period,
                    category,
                    period.label,
                    count,
                  )
                }
              >
                View transactions
              </Button>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
}

export interface SegmentDrilldownState {
  period: string;
  category: SaleCategory;
  periodLabel: string;
  expectedCount: number;
}

export function SalesSegmentTransactionsDialog({
  open,
  drilldown,
  transactions,
  loading,
  onClose,
}: {
  open: boolean;
  drilldown: SegmentDrilldownState | null;
  transactions: Array<{
    id: string;
    date: string;
    productName: string;
    email: string;
    amount: number;
    saleType: string;
  }>;
  loading: boolean;
  onClose: () => void;
}) {
  const title = drilldown
    ? `${CATEGORY_LABELS[drilldown.category]} · ${drilldown.periodLabel}`
    : 'Transactions';

  let dialogBody: React.ReactNode;
  if (loading) {
    dialogBody = (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress size={28} />
      </Box>
    );
  } else if (transactions.length === 0) {
    dialogBody = (
      <Typography color="text.secondary" py={4} textAlign="center">
        No transactions found for this segment.
      </Typography>
    );
  } else {
    dialogBody = (
      <TableContainer sx={{ maxHeight: 420 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Product</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell align="right" width={100}>
                Details
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transactions.map((tx) => (
              <TableRow key={tx.id} hover>
                <TableCell>{tx.date}</TableCell>
                <TableCell>{tx.productName || '—'}</TableCell>
                <TableCell sx={{ maxWidth: 180 }}>{tx.email || '—'}</TableCell>
                <TableCell>{tx.saleType}</TableCell>
                <TableCell align="right">{formatCurrency(tx.amount)}</TableCell>
                <TableCell align="right">
                  <Button
                    component="a"
                    href={transactionDetailUrl(tx.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    variant="outlined"
                    endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                    sx={{
                      py: 0.25,
                      fontSize: '0.75rem',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    View
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, borderTop: `4px solid ${brand.slate}` },
      }}
    >
      <DialogTitle sx={{ pr: 6, bgcolor: 'rgba(55, 71, 79, 0.06)' }}>
        {title}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 12, top: 12 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {dialogBody}
        {!loading && transactions.length > 0 && (
          <Box sx={{ px: 2, py: 1.5, bgcolor: 'action.hover' }}>
            <Typography variant="caption" color="text.secondary">
              {transactions.length} transaction
              {transactions.length === 1 ? '' : 's'}
              {drilldown && drilldown.expectedCount !== transactions.length
                ? ` (chart showed ${drilldown.expectedCount})`
                : ''}
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function UntaggedRenewalsDialog({
  open,
  transactions,
  loading,
  onClose,
}: {
  open: boolean;
  transactions: Array<{
    id: string;
    date: string;
    productName: string;
    email: string;
    amount: number;
    saleType: string;
  }>;
  loading: boolean;
  onClose: () => void;
}) {
  let dialogBody: React.ReactNode;
  if (loading) {
    dialogBody = (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress size={28} />
      </Box>
    );
  } else if (transactions.length === 0) {
    dialogBody = (
      <Typography color="text.secondary" py={4} textAlign="center">
        No untagged subscription charges in this date range.
      </Typography>
    );
  } else {
    dialogBody = (
      <TableContainer sx={{ maxHeight: 420 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Transaction ID</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Product</TableCell>
              <TableCell>Email</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell align="right" width={100}>
                Details
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {transactions.map((tx) => (
              <TableRow key={tx.id} hover>
                <TableCell
                  sx={{
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    maxWidth: 140,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {tx.id || '—'}
                </TableCell>
                <TableCell>{tx.date}</TableCell>
                <TableCell>{tx.productName || '—'}</TableCell>
                <TableCell sx={{ maxWidth: 180 }}>{tx.email || '—'}</TableCell>
                <TableCell align="right">{formatCurrency(tx.amount)}</TableCell>
                <TableCell align="right">
                  <Button
                    component="a"
                    href={transactionDetailUrl(tx.id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    variant="outlined"
                    endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                    sx={{
                      py: 0.25,
                      fontSize: '0.75rem',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    View
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, borderTop: `4px solid ${brand.slate}` },
      }}
    >
      <DialogTitle sx={{ pr: 6, bgcolor: 'rgba(55, 71, 79, 0.06)' }}>
        Untagged subscription charges
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 12, top: 12 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {!loading && transactions.length > 0 && (
          <Box sx={{ px: 2, pt: 2, pb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Subscription charges older than 40 days without a billing reason
              tag. These may be renewals that were not tagged at creation time.
            </Typography>
          </Box>
        )}
        {dialogBody}
        {!loading && transactions.length > 0 && (
          <Box sx={{ px: 2, py: 1.5, bgcolor: 'action.hover' }}>
            <Typography variant="caption" color="text.secondary">
              {transactions.length} transaction
              {transactions.length === 1 ? '' : 's'}
            </Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
